import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { chaveDaDescricao, devePerguntar, irmasNaMemoria } from '@domain/finance'
import type { CategoryRule, RuleDecision } from '@domain/finance'
import { addMonths, isReferenceMonth } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { currentReferenceMonth, todayInHousehold } from '@shared/date/timezone'
import { Glass } from '@ui/glass/Glass'
import { TransactionsList } from '@ui/finance/TransactionsList'
import { monthLabel, shortMonthLabel } from '@ui/finance/labels'
import { monthChips } from '@ui/finance/month-chips'
import { filtersFrom, searchFor } from '@ui/finance/search-params'
import type { TransacoesSearch } from '@ui/finance/search-params'
import type { TransactionFilters } from '@ui/finance/filters'
import type { NewManualTransaction } from '@domain/ports/finance-repository'
import { useEffect, useState } from 'react'
import { NovaTransacao } from '@ui/finance/NovaTransacao'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { useMonthlyBook } from '@ui/finance/use-monthly-book'
import { PerguntaDeRegra } from '@ui/finance/PerguntaDeRegra'
import { BotaoDeIcone } from '@ui/shell/BotaoDeIcone'
import { useAtalhoDaTela } from '@ui/shell/teclas'
import { IconeCartao, IconeMais, IconeTriagem } from '@ui/shell/icones'

/**
 * A aba de Transações, na versão mínima da Fase 2: o seletor de mês, os dois
 * totais e as duas listas. O que `Docs/04-Design/Telas/Transações.md` desenha
 * por inteiro — busca, filtros, faturas, agrupamento por dia, FAB — é a Fase 4.
 *
 * **O mês vive na URL** (`/transacoes?mes=2026-07`), como a nota manda: assim
 * recarregar, voltar e compartilhar link são previsíveis, e sai de graça. O que
 * chega de lá é `unknown` de verdade — digitado, colado, ou de um link de outro
 * ano —, então passa por `isReferenceMonth` antes de virar mês. Um `mes` que
 * não existe cai no mês corrente em vez de derrubar a tela.
 *
 * **E o mês corrente sai de `shared/date`**, nunca de `new Date()` aqui: às 21h
 * do dia 30 o fuso do navegador já viraria o mês, e a tela abriria em julho
 * mostrando junho.
 */

/**
 * Os filtros da tela, lidos da URL. A busca já vem pela allowlist da rota
 * (`normalizeSearch`), então aqui é só traduzir — ver `search-params.ts`.
 */
function useFilters(): TransactionFilters {
  const search = useSearch({ strict: false })
  return filtersFrom(search as TransacoesSearch)
}

function useSelectedMonth(): ReferenceMonth {
  // `strict: false` porque esta tela não importa a própria rota: `router.tsx`
  // importa este componente, e o caminho de volta fecharia um ciclo de módulos.
  const search = useSearch({ strict: false })
  const mes: unknown = (search as Record<string, unknown>)['mes']
  return isReferenceMonth(mes) ? mes : currentReferenceMonth()
}

export function Transacoes() {
  const month = useSelectedMonth()
  const filters = useFilters()
  const navigate = useNavigate()
  const book = useMonthlyBook(month)
  const { transactionRepository, categoryRuleRepository } = useFinanceRepositories()
  const [lancando, setLancando] = useState(false)

  /**
   * Grava a categoria e aplica no livro já carregado.
   *
   * **A ordem importa e é esta: grava, depois corrige a tela.** O contrário —
   * corrigir antes e desfazer se falhar — mostraria a linha saindo da fila de
   * trabalho e voltando, e nesta tela sair da fila é o feedback de que deu
   * certo. Quem espera é uma escrita de um campo.
   *
   * O `patchEnrichment` existe para não reler o mês inteiro: ver o comentário
   * dele em `use-monthly-book.ts`.
   *
   * ## Parcelamento: escreve as irmãs AQUI, mesmo tendo o gatilho
   *
   * `onTransactionWrite` propagaria sozinho — e continua sendo ele quem alcança
   * o que esta tela não carregou e as parcelas que ainda vão chegar. Mas ele é
   * um round-trip depois, e nesse meio-tempo a tela mostraria três linhas ainda
   * sem categoria e uma badge que não se mexeu. Numa tela cujo feedback de
   * sucesso é a linha **sair da fila**, isso lê como "não funcionou".
   *
   * As irmãs estão na memória sem custo nenhum: as parcelas de uma compra
   * dividem a data da compra como competência, então o mês carregado já tem o
   * grupo inteiro (medido: 124 de 124). O lote é uma escrita só.
   */
  async function categorizar(id: string, categoryId: string | null) {
    if (book.status !== 'loaded') return
    const alvo = book.competence.find((t) => t.id === id) ?? book.cash.find((t) => t.id === id)
    const irmas = alvo === undefined ? [] : irmasNaMemoria([...book.competence, ...book.cash], alvo)
    const ids = irmas.length > 1 ? [...new Set(irmas.map((t) => t.id))] : [id]

    // **A escolha à mão apaga a procedência automática** (Fase 10): quem
    // decidiu foi gente, e o azul quer dizer "confira isto".
    const categorySource = { kind: 'manual' as const, ruleId: null, confirmedAt: null }

    if (ids.length > 1) {
      await transactionRepository.updateEnrichmentMany(ids, { categoryId, categorySource })
    } else {
      await transactionRepository.updateEnrichment(id, { categoryId, categorySource })
    }

    for (const alvoId of ids) book.patchEnrichment(alvoId, { categoryId, categorySource })

    // A pergunta vem DEPOIS de gravar, e só quando há o que perguntar: tirar a
    // categoria não ensina nada, e descrição que já tem regra (ou "nunca") não
    // pergunta de novo.
    const descricao = alvo?.source.description ?? ''
    if (categoryId === null || !devePerguntar(descricao, regras)) return
    setPergunta({
      descricao,
      categoria: book.categories.find((c) => c.id === categoryId)?.name ?? 'esta categoria',
      key: chaveDaDescricao(descricao),
      categoryId,
    })
  }

  /**
   * Conferir o que a tabela de regras aplicou — uma linha, ou a fila inteira.
   *
   * Só carimba a data: a categoria continua sendo a que a regra pôs, e o
   * registro de que **o app** decidiu fica. Conferir não é escolher.
   */
  async function conferir(ids: string[]) {
    if (book.status !== 'loaded' || ids.length === 0) return
    await transactionRepository.confirmAutoCategory(ids)
    const todas = [...book.competence, ...book.cash]
    for (const alvoId of ids) {
      const fonte = todas.find((t) => t.id === alvoId)?.enrichment.categorySource
      if (fonte == null) continue
      // `'agora'` como marca otimista, igual ao `readAt` dos avisos: a data de
      // verdade é a do servidor, e chega na próxima leitura.
      book.patchEnrichment(alvoId, { categorySource: { ...fonte, confirmedAt: 'agora' } })
    }
  }

  /** Muda os filtros **sem** trocar de mês. */
  function filtrar(novos: TransactionFilters) {
    void navigate({ to: '/transacoes', search: searchFor(month, novos) })
  }

  /**
   * Cria o lançamento e **recarrega o mês** — ao contrário de categorizar, que
   * corrige o livro no lugar.
   *
   * Aqui a transação nova pode nem pertencer ao mês aberto: quem lança uma
   * despesa de julho estando em agosto não deve vê-la aparecer na lista de
   * agosto. Recarregar deixa os recortes de competência e caixa decidirem, que é
   * o trabalho deles — e a alternativa seria refazer `isInCompetence` aqui, uma
   * terceira vez.
   */
  async function lancar(input: NewManualTransaction) {
    if (book.status !== 'loaded') return
    await transactionRepository.createManual(input)
    book.reload()
  }

  /**
   * A tabela de regras, lida uma vez (Fase 10).
   *
   * Ela serve para uma pergunta só nesta tela — *"esta descrição já tem
   * regra?"* —, e a resposta não muda enquanto a tela está aberta, tirando as
   * regras que a própria pessoa cria aqui. Por isso é estado, e não leitura por
   * categorização.
   */
  const [regras, setRegras] = useState<CategoryRule[]>([])
  const [pergunta, setPergunta] = useState<
    { descricao: string; categoria: string; key: string; categoryId: string } | null
  >(null)

  useEffect(() => {
    let vivo = true
    void categoryRuleRepository
      .list()
      .then((lidas) => {
        if (vivo) setRegras(lidas)
      })
      .catch((erro: unknown) => {
        // Sem a tabela, a tela inteira continua funcionando: o que se perde é a
        // pergunta "valer sempre?", e nunca a categorização.
        console.error('Transações: a tabela de regras não carregou', erro)
      })
    return () => {
      vivo = false
    }
  }, [categoryRuleRepository])

  /** Grava a resposta da barra e recarrega a tabela. */
  function responder(decision: RuleDecision) {
    const atual = pergunta
    setPergunta(null)
    if (atual === null) return
    void categoryRuleRepository
      .upsert({
        key: atual.key,
        label: atual.descricao,
        decision,
        origin: 'learned',
        scope: 'transaction',
      })
      .then(() => categoryRuleRepository.list())
      .then(setRegras)
      .catch((erro: unknown) => {
        console.error('Transações: a regra não foi gravada', erro)
      })
  }

  /** Muda de mês **levando os filtros junto** — eles vivem na URL. */
  function irPara(mes: ReferenceMonth) {
    void navigate({ to: '/transacoes', search: searchFor(mes, filters) })
  }

  // ← e → andam de mês, pelo mesmo `irPara` dos chips: o filtro vai junto.
  useAtalhoDaTela('ArrowLeft', 'mês anterior', () => {
    irPara(addMonths(month, -1))
  })
  useAtalhoDaTela('ArrowRight', 'mês seguinte', () => {
    irPara(addMonths(month, 1))
  })

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="titulo text-2xl">Transações</h1>
        <div className="fileira-de-icones">
          {/* O FAB de `Docs/04-Design/Telas/Transações.md`. Botão de verdade no
              cabeçalho, e não um círculo flutuante sobre a lista: a TabBar já
              ocupa a faixa de baixo, e um flutuante ali cobriria transação — a
              nota pede o FAB, não que ele tape o conteúdo. */}
          <BotaoDeIcone
            rotulo={lancando ? 'Fechar a nova transação' : 'Nova transação'}
            data-atalho-novo
            aria-expanded={lancando}
            onClick={() => {
              setLancando(!lancando)
            }}
          >
            {/* O mesmo `+` girado 45° vira ✕: é o mesmo botão que abre e fecha,
                e trocar de desenho faria parecer que são dois. */}
            <span
              className={`transition-transform ${lancando ? 'rotate-45' : ''}`}
              style={{ transitionDuration: 'var(--duration-fast)' }}
            >
              <IconeMais />
            </span>
          </BotaoDeIcone>
          {/* A entrada da classificação em lote. Fica aqui, e não no
              Dashboard, porque é sobre a lista: quem repara que o mês está
              cheio de "sem categoria" está olhando para esta tela. */}
          <BotaoDeIcone as={Link} to="/classificar" rotulo="Classificar gastos">
            <IconeTriagem />
          </BotaoDeIcone>
          <BotaoDeIcone as={Link} to="/cadastros" rotulo="Cadastros">
            <IconeCartao />
          </BotaoDeIcone>
        </div>
      </header>

      {/* Os chips substituem os botões "Mês anterior"/"Próximo mês" da versão
          mínima da Fase 2 — `Docs/04-Design/Telas/Transações.md`. A janela
          termina um mês à frente do selecionado justamente para que avançar
          continue possível sem um controle à parte. */}
      <nav aria-label="Mês" className="mb-4 flex flex-wrap gap-2 text-sm">
        {monthChips(month, currentReferenceMonth()).map((chip) => {
          const selecionado = chip === month
          return (
            <Glass
              key={chip}
              as="button"
              // **`lens` SÓ no selecionado, e isto resolve um conflito entre
              // duas notas.** `Transações.md` diz que "os chips usam lens — é
              // um dos poucos lugares que merecem"; `Liquid Glass.md` limita a
              // **3 lens por tela**. Com quatro chips, obedecer as duas exige
              // escolher — e o selecionado é quem merece, porque é o único que
              // carrega estado. Os outros são `flat`, que é sólido: nenhum
              // custo de composição, e o contraste entre eles vira o próprio
              // indicador de seleção.
              level={selecionado ? 'lens' : 'flat'}
              // `alvo` porque o chip de mês é a navegação principal desta
              // tela — trocar de mês é o gesto mais repetido aqui.
              className={`alvo rounded-full px-4 ${selecionado ? 'font-semibold' : ''}`}
              {...({
                type: 'button',
                'aria-current': selecionado ? 'true' : undefined,
                onClick: () => {
                  irPara(chip)
                },
              } as Record<string, unknown>)}
            >
              {shortMonthLabel(chip, currentReferenceMonth().slice(0, 4))}
            </Glass>
          )
        })}
      </nav>

      {lancando && book.status === 'loaded' && (
        <NovaTransacao
          accounts={book.accounts}
          categories={book.categories}
          today={todayInHousehold()}
          onCreate={lancar}
          onClose={() => {
            setLancando(false)
          }}
        />
      )}

      <Glass level="frost" className="p-5">
        {book.status === 'loading' && (
          <p role="status" className="text-sm text-[var(--text-muted)]">
            Carregando {monthLabel(month)}…
          </p>
        )}
        {book.status === 'failed' && (
          <p role="alert" className="text-sm text-[var(--text-muted)]">
            Não foi possível carregar as transações agora.
          </p>
        )}
        {book.status === 'loaded' && (
          <TransactionsList
            month={month}
            competence={book.competence}
            cash={book.cash}
            categories={book.categories}
            invoices={book.invoices}
            creditCards={book.creditCards}
            accounts={book.accounts}
            today={todayInHousehold()}
            filters={filters}
            onFiltersChange={filtrar}
            onSelectMonth={irPara}
            onCategoriasMudaram={book.reload}
            onCategorize={categorizar}
            onConfirmar={conferir}
          />
        )}
      </Glass>

      {/* Fora do `Glass`: `backdrop-filter` faz do cartão a referência de todo
          `fixed` lá dentro (decisão 242e), e a barra ficaria presa nele. */}
      <PerguntaDeRegra
        pergunta={pergunta}
        onSempre={() => {
          if (pergunta !== null) {
            responder({ kind: 'category', categoryId: pergunta.categoryId })
          }
        }}
        onNunca={() => {
          responder({ kind: 'never' })
        }}
        onFechar={() => {
          setPergunta(null)
        }}
      />
    </section>
  )
}
